import { useI18n } from '../../hooks/useI18n';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { SUPPORTED_LANGUAGES } from '../../i18n/supportedLanguages';
import { useTranslation } from 'react-i18next';

export type SupportedLanguageCode = 'cs' | 'en' | 'ru';

export const CLIENT_LANGUAGE_OPTIONS: Array<{ code: SupportedLanguageCode; label: string; flag: string }> = [
  {
    code: 'cs',
    // "CZ" is the label user asked for (even though language code is cs)
    label: 'CZ',
    flag: SUPPORTED_LANGUAGES.find((l) => l.code === 'cs')?.flag ?? '🇨🇿',
  },
  {
    code: 'en',
    label: 'EN',
    flag: SUPPORTED_LANGUAGES.find((l) => l.code === 'en')?.flag ?? '🇬🇧',
  },
  {
    code: 'ru',
    label: 'RU',
    flag: SUPPORTED_LANGUAGES.find((l) => l.code === 'ru')?.flag ?? '🇷🇺',
  },
];

export function formatClientLanguage(code: string | null | undefined): string {
  const opt = CLIENT_LANGUAGE_OPTIONS.find((o) => o.code === code);
  return opt ? `${opt.flag} ${opt.label}` : '';
}

export type LanguageSelectorProps = {
  variant?: 'default' | 'compact';
  allowedLanguages?: string[];
  mode?: 'dashboard' | 'i18n-only';
};

function normalizeLanguageCode(code: string | null | undefined): string {
  return String(code || '').trim().toLowerCase().split('-')[0];
}

type MenuLanguage = { code: string; native_name: string; flag?: string; name?: string };

function getMenuLanguages(allowedLanguages?: string[]): MenuLanguage[] {
  const allowed = (allowedLanguages || []).map(normalizeLanguageCode).filter(Boolean);
  if (allowed.length === 0) return SUPPORTED_LANGUAGES;
  const filtered = SUPPORTED_LANGUAGES.filter((l) => allowed.includes(normalizeLanguageCode(l.code)));
  return filtered.length > 0 ? filtered : SUPPORTED_LANGUAGES;
}

function LanguageSelectorBase({
  variant,
  menuLanguages,
  currentLanguage,
  onChange,
  t,
}: {
  variant: 'default' | 'compact';
  menuLanguages: MenuLanguage[];
  currentLanguage: string;
  onChange: (code: string) => void;
  t: (key: string, options?: any) => string;
}) {
  const getCurrentLanguageDisplay = () => {
    const lang = menuLanguages.find((l) => l.code === currentLanguage);
    return lang?.native_name || menuLanguages[0].native_name;
  };

  const getCompactLanguageDisplay = () => {
    const opt = CLIENT_LANGUAGE_OPTIONS.find((o) => o.code === currentLanguage);
    if (opt) return `${opt.flag} ${opt.label}`;
    const lang = SUPPORTED_LANGUAGES.find((l) => l.code === currentLanguage);
    const flag = lang?.flag ?? '';
    const code = String(currentLanguage || '').toUpperCase();
    return `${flag} ${code}`.trim();
  };

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button
        className={
          variant === 'compact'
            ? 'inline-flex max-w-full items-center gap-2 rounded-control bg-white/80 px-2.5 py-1.5 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300/80 hover:bg-white'
            : 'inline-flex items-center gap-2 rounded-control bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
        }
        aria-label={t('select_language', { ns: 'common' })}
      >
        <span className={variant === 'compact' ? 'truncate' : undefined}>
          {variant === 'compact'
            ? getCompactLanguageDisplay()
            : `${SUPPORTED_LANGUAGES.find((l) => l.code === currentLanguage)?.flag} ${getCurrentLanguageDisplay()}`}
        </span>
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-10 mt-2 max-h-96 w-56 overflow-auto rounded-surface bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          {menuLanguages.map((language) => (
            <Menu.Item key={language.code}>
              {({ active }) => (
                <button
                  onClick={() => onChange(language.code)}
                  className={`
                    mx-1 flex w-[calc(100%-0.5rem)] items-center rounded-control px-3 py-2 text-sm
                    ${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'}
                  `}
                >
                  <span className="flex-grow">
                    <span className="flex items-center gap-2 font-medium">
                      <span>{language.flag}</span>
                      <span>{language.native_name}</span>
                    </span>
                  </span>
                  {currentLanguage === language.code && (
                    <CheckIcon className="h-4 w-4 text-primary-600" />
                  )}
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

function DashboardLanguageSelector({ variant, allowedLanguages }: { variant: 'default' | 'compact'; allowedLanguages?: string[] }) {
  const { t, currentLanguage, setLanguage, i18n } = useI18n();

  const menuLanguages = getMenuLanguages(allowedLanguages);

  const handleLanguageChange = (code: string) => {
    i18n
      .changeLanguage(code)
      .then(() => {
        setLanguage(code);
        toast.success(t('toasts.language_changed', { ns: 'common', language: SUPPORTED_LANGUAGES.find((l) => l.code === code)?.name || code }));
      })
      .catch(() => {
        toast.error(t('toasts.language_change_failed', { ns: 'common' }));
      });
  };

  return (
    <LanguageSelectorBase
      variant={variant}
      menuLanguages={menuLanguages}
      currentLanguage={currentLanguage}
      onChange={handleLanguageChange}
      t={t}
    />
  );
}

function I18nOnlyLanguageSelector({ variant, allowedLanguages }: { variant: 'default' | 'compact'; allowedLanguages?: string[] }) {
  const { t, i18n } = useTranslation();
  const menuLanguages = getMenuLanguages(allowedLanguages);
  const currentLanguage = normalizeLanguageCode(i18n.resolvedLanguage || i18n.language) || menuLanguages[0].code;

  const handleLanguageChange = (code: string) => {
    i18n
      .changeLanguage(code)
      .then(() => {
        toast.success(t('toasts.language_changed', { ns: 'common', language: SUPPORTED_LANGUAGES.find((l) => l.code === code)?.name || code }));
      })
      .catch(() => {
        toast.error(t('toasts.language_change_failed', { ns: 'common' }));
      });
  };

  return (
    <LanguageSelectorBase
      variant={variant}
      menuLanguages={menuLanguages}
      currentLanguage={currentLanguage}
      onChange={handleLanguageChange}
      t={t}
    />
  );
}

export function LanguageSelector({ variant = 'default', allowedLanguages, mode = 'dashboard' }: LanguageSelectorProps) {
  if (mode === 'i18n-only') {
    return <I18nOnlyLanguageSelector variant={variant} allowedLanguages={allowedLanguages} />;
  }

  return <DashboardLanguageSelector variant={variant} allowedLanguages={allowedLanguages} />;
}